// One assistant with modes (REDISENO_UI.md 3.7, step C18 of the redesign).
//
// Under ui_v2 the shell draws a single docked AI panel on the right. Pages
// that had their own AI panel (the Workbench dock, the Logic Copilot, the
// process map chat) register a "mode" here instead of drawing a panel of
// their own, and render that same component through a portal into the slot
// the shell panel gives the mode. The component, its state, its streaming and
// the context it sends stay in the page: only where it is drawn changes.
//
// Without the flag nothing registers and nothing reads this store.
import { useEffect } from 'react';
import { create } from 'zustand';
import { useAssistantStore } from '../store/assistantStore';

/** The platform assistant (NexusAssistant). Always available. */
export const GENERAL_MODE = 'general';

export type AssistantModeIcon = 'analyst' | 'notebook' | 'copilot' | 'map';

export interface AssistantModeDef {
  /** Stable id, e.g. `workbench.analyst`. */
  id: string;
  /** Tab label in the panel. */
  label: string;
  icon: AssistantModeIcon;
  /** What this mode sees, shown as chips under the tabs: the open resource,
   *  the active filter. Only what the mode really sends as context. */
  context?: string[];
  /** Lower sorts first among the page's modes. */
  order?: number;
}

interface AssistantModesState {
  modes: AssistantModeDef[];
  /** Selected mode; GENERAL_MODE when no page mode is chosen. */
  mode: string;
  /** DOM node each mode renders into while the panel is open. */
  slots: Record<string, HTMLElement | null>;
  register: (def: AssistantModeDef) => void;
  unregister: (id: string) => void;
  setMode: (id: string) => void;
  setSlot: (id: string, el: HTMLElement | null) => void;
}

const sortModes = (modes: AssistantModeDef[]) =>
  [...modes].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

export const useAssistantModes = create<AssistantModesState>((set, get) => ({
  modes: [],
  mode: GENERAL_MODE,
  slots: {},
  register: (def) => set((s) => ({
    modes: sortModes([...s.modes.filter((m) => m.id !== def.id), def]),
  })),
  unregister: (id) => {
    const wasActive = get().mode === id;
    set((s) => ({ modes: s.modes.filter((m) => m.id !== id), mode: wasActive ? GENERAL_MODE : s.mode }));
    // The page that owned the open mode went away: close the panel rather
    // than drop the user into a different assistant they did not ask for.
    if (wasActive) useAssistantStore.getState().setOpen(false);
  },
  setMode: (id) => set({ mode: id }),
  setSlot: (id, el) => {
    if (get().slots[id] === el) return;
    set((s) => ({ slots: { ...s.slots, [id]: el } }));
  },
}));

/** The mode the panel shows: the selected one if it is still registered. */
export function effectiveMode(s: Pick<AssistantModesState, 'modes' | 'mode'>): string {
  return s.mode === GENERAL_MODE || s.modes.some((m) => m.id === s.mode) ? s.mode : GENERAL_MODE;
}

/** Opens the single panel in a mode. */
export function openAssistantMode(id: string = GENERAL_MODE) {
  useAssistantModes.getState().setMode(id);
  useAssistantStore.getState().setOpen(true);
}

/** The rail button and the shortcut: close if open, else open in the page's
 *  first mode (the assistant "knows where the user is"), or General. */
export function toggleAssistantPanel() {
  const { open, setOpen } = useAssistantStore.getState();
  if (open) { setOpen(false); return; }
  const { modes } = useAssistantModes.getState();
  openAssistantMode(modes[0]?.id ?? GENERAL_MODE);
}

/**
 * Registers a page's assistant mode while the page is mounted (pass null to
 * not offer it). Returns whether the mode is the one showing and the DOM node
 * to portal the mode's component into (null while the panel is closed or the
 * mode is not registered).
 */
export function useAssistantMode(def: AssistantModeDef | null) {
  const id = def?.id ?? null;
  const register = useAssistantModes((s) => s.register);
  const unregister = useAssistantModes((s) => s.unregister);

  // Register on mount and unregister on unmount; label and chips update in
  // place so a rename does not close the panel.
  useEffect(() => {
    if (!id) return;
    return () => unregister(id);
  }, [id, unregister]);
  const signature = def ? `${def.label}|${def.icon}|${def.order ?? 0}|${(def.context || []).join('|')}` : '';
  useEffect(() => {
    if (def) register(def);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, signature, register]);

  const open = useAssistantStore((s) => s.open);
  const current = useAssistantModes(effectiveMode);
  const slot = useAssistantModes((s) => (id ? s.slots[id] ?? null : null));
  const active = !!id && open && current === id;
  return {
    /** The panel is open on this mode. */
    active,
    /** Where to portal the mode's component; kept while the panel is open so
     *  switching tabs does not lose a conversation. */
    slot: open && id ? slot : null,
    open: () => { if (id) openAssistantMode(id); },
    close: () => useAssistantStore.getState().setOpen(false),
    toggle: () => {
      if (!id) return;
      if (active) useAssistantStore.getState().setOpen(false);
      else openAssistantMode(id);
    },
  };
}
